// Library methods
import { useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import SelectDropdown from "src/components/shared/inputs/SelectDropdown/SelectDropdown";
import TextInputField from "src/components/shared/inputs/TextInputField/TextInputField";
import ChoiceCard from "../ChoiceCard/ChoiceCard";
import FormField from "../FormField/FormField";
import { CloseIcon, PlusIcon } from "src/assets/icons";

// Utils
import {
  WORKLIST_LOOKAHEAD_DAYS_MAX,
  WorklistTimingValues,
  isValidTimeOfDay,
  pollIntervalOptions,
} from "src/utils/emr/worklistTimingHelper";

// Styles
import {
  ModalCustomConfirmLink,
  getRequiredErrorColor,
  getSurfaceTokens,
} from "src/styles/mui/muiStylesHelper";

type WorklistTimingType = {
  idPrefix: string;
  value: WorklistTimingValues;
  onChange: (value: WorklistTimingValues) => void;
};

const WorklistTiming = ({ idPrefix, value, onChange }: WorklistTimingType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { onSurface, mutedText, inputBorder } = getSurfaceTokens(theme);
  const [newTime, setNewTime] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(value.lookaheadDays !== "0");

  const intervalOptions = pollIntervalOptions(value.pollIntervalMinutes);

  const lookaheadDays = Number(value.lookaheadDays);
  const isLookaheadInvalid =
    value.lookaheadDays === "" ||
    lookaheadDays < 0 ||
    lookaheadDays > WORKLIST_LOOKAHEAD_DAYS_MAX;

  const addTime = () => {
    if (!isValidTimeOfDay(newTime) || value.importTimes.includes(newTime)) {
      return;
    }
    onChange({ ...value, importTimes: [...value.importTimes, newTime].sort() });
    setNewTime("");
  };

  return (
    <Box display="flex" flexDirection="column" gap="8px">
      <ChoiceCard
        name={`${idPrefix}-timing`}
        value="Interval"
        checked={value.mode === "Interval"}
        title={t("Keep it up to date")}
        badge={t("Recommended")}
        onSelect={() => onChange({ ...value, mode: "Interval" })}
      >
        <Box display="flex" alignItems="center" gap="8px" flexWrap="wrap">
          <Typography
            id={`${idPrefix}-interval-label`}
            fontSize="13px"
            color={onSurface}
          >
            {t("Every")}
          </Typography>
          <Box width="88px">
            <SelectDropdown
              id={`${idPrefix}-interval-dropdown`}
              labelId={`${idPrefix}-interval-label`}
              dropdownOptions={intervalOptions}
              value={String(value.pollIntervalMinutes)}
              disabled={value.mode !== "Interval"}
              handleChange={(event) =>
                onChange({
                  ...value,
                  pollIntervalMinutes: Number(event.target.value),
                })
              }
            />
          </Box>
          <Typography fontSize="13px" color={onSurface}>
            {t("min, today's appointments")}
          </Typography>
        </Box>
      </ChoiceCard>

      <ChoiceCard
        name={`${idPrefix}-timing`}
        value="Times"
        checked={value.mode === "Times"}
        title={t("At set times")}
        description={t(
          "Hub PC's local time. Patients booked after the last time wait for the next one."
        )}
        onSelect={() => onChange({ ...value, mode: "Times" })}
      >
        {value.mode === "Times" && (
          <Box display="flex" flexDirection="column" gap="8px" mt="4px">
            <Box display="flex" gap="6px" flexWrap="wrap">
              {value.importTimes.map((time) => (
                <Box
                  key={time}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "2px",
                    paddingLeft: "10px",
                    borderRadius: "999px",
                    border: `1px solid ${inputBorder}`,
                  }}
                >
                  <Typography fontSize="13px" fontWeight={700}>
                    {time}
                  </Typography>
                  <IconButton
                    aria-label={`${t("Remove")} ${time}`}
                    size="small"
                    sx={{ color: onSurface }}
                    onClick={() =>
                      onChange({
                        ...value,
                        importTimes: value.importTimes.filter(
                          (item) => item !== time
                        ),
                      })
                    }
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              ))}
            </Box>
            <Box display="flex" gap="8px" alignItems="center">
              <TextField
                id={`${idPrefix}-new-time-input`}
                type="time"
                size="small"
                value={newTime}
                onChange={(event) => setNewTime(event.target.value)}
                inputProps={{ "aria-label": t("New time") }}
                sx={{
                  "& .MuiInputBase-root": { color: onSurface },
                  "& fieldset": { borderColor: inputBorder },
                }}
              />
              <Button
                startIcon={<PlusIcon />}
                sx={ModalCustomConfirmLink}
                disabled={!isValidTimeOfDay(newTime)}
                onClick={addTime}
              >
                {t("Add time")}
              </Button>
            </Box>
            {value.importTimes.length === 0 && (
              <Typography fontSize="12px" color={getRequiredErrorColor(theme)}>
                {t("Add at least one time.")}
              </Typography>
            )}
          </Box>
        )}
      </ChoiceCard>

      <Box>
        <Button
          sx={ModalCustomConfirmLink}
          aria-expanded={showAdvanced}
          onClick={() => setShowAdvanced((previous) => !previous)}
        >
          {t("Advanced")}
        </Button>
        {showAdvanced && (
          <Box display="flex" flexDirection="column" gap="4px" mt="4px">
            <Box maxWidth="220px">
              <FormField
                label={t("Also read the next (days)")}
                htmlFor={`${idPrefix}-lookahead-input`}
              >
                <TextInputField
                  id={`${idPrefix}-lookahead-input`}
                  placeholder="0"
                  value={value.lookaheadDays}
                  maxLength={2}
                  error={isLookaheadInvalid}
                  onChange={(event) =>
                    onChange({
                      ...value,
                      lookaheadDays: event.target.value.replace(/[^0-9]/g, ""),
                    })
                  }
                />
              </FormField>
            </Box>
            <Typography fontSize="12px" color={mutedText}>
              {t("0 is today only. At most 14.")}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default WorklistTiming;
